import React from 'react'
import { injectIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import LazyLoad from 'react-lazyload'

import { AppContext } from 'src/AppContext'
import { ToggleableQuery } from 'src/lib/toggleable-query'
import { asyncComponent } from 'src/lib/async-component'
import { BREAK_MD } from 'src/lib/media-queries'
import { setDigitalData } from 'src/lib/digital-data'

import { Hero } from 'src/components/Hero/Hero'
import { EventsCarousel, MAX_EVENT_CARDS } from 'src/components/events-carousel/index'
import { MOBILE_EVENT_IMAGE_HEIGHT, MOBILE_EVENT_IMAGE_WIDTH } from 'src/components/event-card/index'
import { MAIN_CATEGORIES } from 'src/lib/categories'
import { trackAmplitude } from 'src/lib/analytics'

import eventsQuery from './home.gql'

import style from './index.css'
import locale from './locale'

const Navbar = asyncComponent(() => (
  import(/* webpackChunkName: "components-navbar", webpackPreload: true */ 'src/components/Navbar/Navbar')
), 'Navbar')

const Search = asyncComponent(() => (
  import(/* webpackChunkName: "components-search" */ 'src/components/search/index')
), 'Search')

const Footer = asyncComponent(() => (
  import(/* webpackChunkName: "components-footer", webpackPrefetch: true */ 'src/components/Footer/Footer')
), 'Footer')

const CovidAlertBanner = asyncComponent(() => (
  import(/* webpackChunkName: "components-covid-alert-banner", webpackPrefetch: true */ 'src/components/CovidAlertBanner/CovidAlertBanner') // eslint-disable-line max-len
), 'CovidAlertBanner')

// eslint-disable-next-line max-len
const ORGANIZERS_LOGOS = 'https://www.universe.com/multiverse/assets/images/templates/index/logos/logos-d4e15f4af0e48a0babd45eab6a100f9d.svg'
const MAX_DISTANCE_KM = 7500

class HomeComponent extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      searchOpened: false,
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0)

    const { prerendering, lat, lon } = SSR_DATA
    if (!prerendering && (!lat || !lon)) {
      alert('Please provide lat / lon arguments') // eslint-disable-line no-alert
    }
  }

  eventsInCity({ data, cityName }) {
    const { formatMessage } = this.props.intl
    let message = formatMessage(locale.eventsInCity)

    if (data && data.thisWeekendEvents) {
      const events = [...data.thisWeekendEvents.events, ...data.trendingEvents.events, ...data.freeEvents.events]
      const cities = new Set([])
      const differentCities = events.find((event) => {
        cities.add(event.geo.city)
        return cities.size > 1
      })
      if (differentCities) {
        message = formatMessage(locale.eventsNearCity)
      }
    }

    return (
      <div className={style.eventsInCity}>
        {message}&nbsp;
        <span
          className={style.cityName}
          onClick={() => this.toggleSearch({ sectionId: 'city_name' })}
          onKeyPress={() => this.toggleSearch({ sectionId: 'city_name' })}
          role="button"
          tabIndex="-1"
        >
          {cityName}
        </span>
      </div>
    )
  }

  toggleSearch = (props = {}) => {
    this.setState({ searchOpened: !this.state.searchOpened, searchSectionId: (props.sectionId || 'search_bar') })
  }

  categories({ cityName }) {
    const { formatMessage } = this.props.intl
    const categories = MAIN_CATEGORIES.map((category) => {
      const title = formatMessage(category.locale)
      const description = formatMessage(category.descriptionLocale)
      // eslint-disable-next-line max-len
      const urlArgs = [
        `categories=${category.value}`,
        `loc=${cityName}`,
        `ll=${encodeURIComponent(`${SSR_DATA.lat},${SSR_DATA.lon}`)}`,
      ].join('&')
      const url = `/explore?${urlArgs}`

      return (
        <Link to={url} href={url} key={category.value} className={style.category}>
          <LazyLoad
            once
            offset={200}
            placeholder={<div className={style.categoryImage} />}
          >
            <img src={category.imageUrl} alt={title} className={style.categoryImage} />
          </LazyLoad>
          <div className={style.categoryTitle}>{title}</div>
          <div className={style.categoryDescription}>{description}</div>
        </Link>
      )
    })

    return (
      <div className={style.categoriesContainer}>
        <MediaQuery maxWidth={BREAK_MD - 1}>
          <div className={style.categoriesTitle}>{formatMessage(locale.exploreCategories)}</div>
          <div className={style.categories}>{categories}</div>
        </MediaQuery>
        <MediaQuery minWidth={BREAK_MD}>
          <div className={style.categoriesTitle}>
            {formatMessage(locale.exploreCategoriesInCity, { cityName })}
          </div>
          <div className={style.categories}>{categories}</div>
        </MediaQuery>
      </div>
    )
  }

  organizers() {
    const { formatMessage } = this.props.intl

    return (
      <div className={style.organizers}>
        <div className={style.organizersTitle}>{formatMessage(locale.organizersTitle)}</div>
        <div className={style.organizersCount}>{formatMessage(locale.organizersCount)}</div>
        <LazyLoad
          once
          offset={200}
          placeholder={<div className={style.organizersLogos} />}
        >
          <div
            className={style.organizersLogos}
            style={{ background: `url(${ORGANIZERS_LOGOS}) center center/contain no-repeat` }}
          />
        </LazyLoad>
      </div>
    )
  }

  render() {
    return (
      <AppContext.Consumer>
        {({ viewer, viewerFetched }) => {
          const { formatMessage } = this.props.intl

          if (viewerFetched) trackAmplitude('Homepage View', {}, viewer)

          setDigitalData({
            channel: 'homepage',
            type: 'Home Page',
            country: SSR_DATA.countryCode,
          })

          if (this.state.searchOpened) {
            return (
              <Search
                closeSearch={this.toggleSearch}
                lat={SSR_DATA.lat}
                lon={SSR_DATA.lon}
                locationQuery={SSR_DATA.cityName}
                viewer={viewer}
                referral="homepage"
                page="homepage"
                sectionId={this.state.searchSectionId}
                history={this.props.history}
              />
            )
          }

          return (
            <ToggleableQuery
              query={eventsQuery}
              key={eventsQuery}
              variables={{
                latitude: SSR_DATA.lat,
                longitude: SSR_DATA.lon,
                limit: MAX_EVENT_CARDS,
                distance: MAX_DISTANCE_KM,
                width: MOBILE_EVENT_IMAGE_WIDTH,
                height: MOBILE_EVENT_IMAGE_HEIGHT,
              }}
            >
              {({ data, loading }) => (
                // eslint-disable-next-line css-modules/no-undef-class
                <div className={style.container}>
                  <Navbar transparent />
                  <CovidAlertBanner />
                  <Hero
                    openSearch={this.toggleSearch}
                    lat={SSR_DATA.lat}
                    lon={SSR_DATA.lon}
                    locationQuery={SSR_DATA.cityName}
                    history={this.props.history}
                  />
                  <MediaQuery maxWidth={BREAK_MD - 1}>
                    {this.eventsInCity({ data, cityName: SSR_DATA.cityName })}
                  </MediaQuery>
                  <div className={style.eventCarousels}>
                    <EventsCarousel
                      loading={loading}
                      title={formatMessage(locale.thisWeekendEvents)}
                      events={data && data.thisWeekendEvents}
                      sectionId="happening_this_weekend"
                      page="homepage"
                      viewAllPath={
                        `/explore?ll=${encodeURIComponent(`${SSR_DATA.lat},${SSR_DATA.lon}`)}&time=this-weekend`
                      }
                    />
                    <EventsCarousel
                      loading={loading}
                      title={formatMessage(locale.trendingEvents)}
                      events={data && data.trendingEvents}
                      sectionId="trending_events"
                      page="homepage"
                      viewAllPath={
                        `/explore?ll=${encodeURIComponent(`${SSR_DATA.lat},${SSR_DATA.lon}`)}`
                      }
                    />
                    <EventsCarousel
                      loading={loading}
                      title={formatMessage(locale.freeEvents)}
                      events={data && data.freeEvents}
                      sectionId="free_events"
                      page="homepage"
                      viewAllPath={
                        `/explore?ll=${encodeURIComponent(`${SSR_DATA.lat},${SSR_DATA.lon}`)}&price=free`
                      }
                    />
                  </div>
                  {this.categories({ cityName: SSR_DATA.cityName })}
                  <MediaQuery minWidth={BREAK_MD}>
                    {this.organizers(data)}
                  </MediaQuery>
                  <Footer />
                </div>
              )}
            </ToggleableQuery>
          )
        }}
      </AppContext.Consumer>
    )
  }
}

export const Home = injectIntl(HomeComponent)
